import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import type { DefaultValues } from 'react-hook-form';
import {
  CustomAttributeDataSchema,
  FileOrRelationSchema,
  NullableStringDateSchema,
} from 'src/schemas/global';
import { z } from 'zod';

// Result type has no default, so every failure here means "not chosen yet".
// errorMap (rather than required_error) also covers the empty-option value,
// which arrives as null and would otherwise read as a raw enum error.
const ControlTypeSchema = z.nativeEnum(
  Risk_Assessment_Result_Control_Type_Enum,
  {
    errorMap: () => ({ message: 'Required' }),
  }
);

export const RiskAssessmentResultSchema = z
  .object({
    AssessmentId: z.string().uuid().nullish(),
    OversightAssessmentId: z.string().uuid().nullish(),
    InternalAuditReportId: z.string().uuid().nullish(),
    RiskIds: z
      .array(
        z.object({
          value: z
            .string({ required_error: 'Required' })
            .uuid({ message: 'Required' }),
        })
      )
      .min(1, { message: 'A rating must be linked to at least 1 risk' }),
    Rating: z.number().int({ message: 'Required' }).nullish(),
    Impact: z.number().int({ message: 'Required' }).nullish(),
    Likelihood: z.number().int({ message: 'Required' }).nullish(),
    ControlType: ControlTypeSchema,
    Rationale: z.string().nullish(),
    TestDate: NullableStringDateSchema,
    files: z.array(FileOrRelationSchema),
  })
  .and(CustomAttributeDataSchema);

export type RiskAssessmentResultFormDataFields = Omit<
  z.infer<typeof RiskAssessmentResultSchema>,
  'resultType'
>;

type RiskAssessmentResultFormDefaults =
  DefaultValues<RiskAssessmentResultFormDataFields>;

// ControlType is deliberately absent: the user must pick Inherent/Residual
// explicitly, because the value cannot be corrected after save (CUS-255).
export const defaultValues: RiskAssessmentResultFormDefaults = {
  AssessmentId: null,
  OversightAssessmentId: null,
  InternalAuditReportId: null,
  RiskIds: [],
  Rating: null,
  Impact: null,
  Likelihood: null,
  files: [],
  CustomAttributeData: null,
  Rationale: '',
  TestDate: new Date().toISOString(),
};

// --- Multi-impact variant ---

const ImpactCategoryRatingSchema = z.object({
  categoryName: z.string(),
  rating: z.number().int().nullish(),
});

export const MultiImpactRiskAssessmentResultSchema = z
  .object({
    AssessmentId: z.string().uuid().nullish(),
    OversightAssessmentId: z.string().uuid().nullish(),
    InternalAuditReportId: z.string().uuid().nullish(),
    RiskIds: z
      .array(
        z.object({
          value: z
            .string({ required_error: 'Required' })
            .uuid({ message: 'Required' }),
        })
      )
      .min(1, { message: 'A rating must be linked to at least 1 risk' }),
    Likelihood: z.number().int().nullish(),
    ImpactRatings: z.array(ImpactCategoryRatingSchema),
    Rating: z.number().int({ message: 'Required' }).nullish(),
    ControlType: ControlTypeSchema,
    Rationale: z.string().nullish(),
    TestDate: NullableStringDateSchema,
    files: z.array(FileOrRelationSchema),
  })
  .and(CustomAttributeDataSchema);

export type MultiImpactRiskAssessmentResultFormDataFields = Omit<
  z.infer<typeof MultiImpactRiskAssessmentResultSchema>,
  'resultType'
>;

export type ImpactCategoryRating = z.infer<typeof ImpactCategoryRatingSchema>;
